import * as React from 'react';
import styles from './HeaderComponent.module.scss';
import logo from '../../resources/images/logo.png';
import { ReactComponent as UserSvg } from '../../resources/images/user.svg';
import Icon from '@ant-design/icons';
import { CaretDownOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import CustomContext from '../../context/CustomContext';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Button, Divider, Menu, message, Space, Tooltip } from 'antd';
import SubMenu from 'antd/lib/menu/SubMenu';
import { ApplicationType, PageType } from '../../model/types';
import authService from '../../services/AuthService';
import { workDocsOAuth2Enabled } from '../../api/CustomAxios';

class HeaderComponent extends React.Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;

    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    /** METHODS **/

    logOut = async () => {
        const { updateAuth } = this.context;
        const { intl } = this.props;

        try {
            await authService.logOut();
            updateAuth();
        } catch (error) {
            message.error(intl.formatMessage({ id: 'status.logoutError' }));
        }
    };

    /*** COMPONENTS ***/

    renderLogo = (): React.ReactElement | undefined => {
        return (
            <Link to="/">
                <img src={logo} alt="Logo" height={32} />
                <span className={styles.bold}>PreCon</span>
                <span>nector</span>
            </Link>
        );
    };

    renderSelectedApp = (application: ApplicationType): React.ReactElement => {
        switch (application) {
            case 'SYNC':
                return <FormattedMessage id="header.apps.sync" />;
            case 'SETUP':
                return <FormattedMessage id="header.apps.setup" />;
            case 'REPORTS':
                return <FormattedMessage id="header.apps.reports" />;
            case 'ADMIN':
                return <FormattedMessage id="header.apps.admin" />;
        }
    };

    renderApps = (): React.ReactElement | undefined => {
        const { auth, application } = this.context;

        if (auth && application) {
            return (
                <Menu theme="dark" mode="horizontal">
                    <SubMenu
                        key="apps"
                        title={
                            <>
                                {this.renderSelectedApp(application)} <CaretDownOutlined />
                            </>
                        }
                        popupOffset={[0, 1]}
                    >
                        <Menu.Item key="sync" hidden={!auth.authorities.includes('ROLE_SYNC')}>
                            <Link to="/sync">
                                <FormattedMessage id="header.apps.sync" />
                            </Link>
                        </Menu.Item>
                        <Menu.Item key="setup" hidden={!auth.authorities.includes('ROLE_SETUP')}>
                            <Link to="/setup">
                                <FormattedMessage id="header.apps.setup" />
                            </Link>
                        </Menu.Item>
                        <Menu.Item key="reports" hidden={!auth.authorities.includes('ROLE_REPORTS') || true}>
                            <Link to="/reports">
                                <FormattedMessage id="header.apps.reports" />
                            </Link>
                        </Menu.Item>
                        <Menu.Item key="admin" hidden={!auth.authorities.includes('ROLE_ADMIN')}>
                            <Link to="/admin">
                                <FormattedMessage id="header.apps.admin" />
                            </Link>
                        </Menu.Item>
                    </SubMenu>
                </Menu>
            );
        }
    };

    renderMenu = (): React.ReactElement | undefined => {
        const { auth, application } = this.context;
        const { pageId } = this.props;
        let selectedKeys = pageId ? [pageId] : [];
        const tooltipAlign = { offset: [0, -19] };

        if (auth && application === 'SYNC') {
            return (
                <Menu theme="dark" mode="horizontal" defaultSelectedKeys={['connections']} selectedKeys={selectedKeys}>
                    <Menu.Item key="connections">
                        <Tooltip
                            title={<FormattedMessage id="header.apps.sync.connections.tooltip" />}
                            align={tooltipAlign}
                        >
                            <Link to="/sync/connections">
                                <FormattedMessage id="header.apps.sync.connections" />
                            </Link>
                        </Tooltip>
                    </Menu.Item>
                    <Menu.Item key="files">
                        <Tooltip title={<FormattedMessage id="header.apps.sync.files.tooltip" />} align={tooltipAlign}>
                            <Link to="/sync/files">
                                <FormattedMessage id="header.apps.sync.files" />
                            </Link>
                        </Tooltip>
                    </Menu.Item>
                </Menu>
            );
        } else if (auth && application === 'SETUP') {
            return (
                <Menu
                    theme="dark"
                    mode="horizontal"
                    defaultSelectedKeys={['projectMembers']}
                    selectedKeys={selectedKeys}
                >
                    <Menu.Item key="projectMembers">
                        <Tooltip
                            title={<FormattedMessage id="header.apps.setup.projectMembers.tooltip" />}
                            align={tooltipAlign}
                        >
                            <Link to="/setup/project-members">
                                <FormattedMessage id="header.apps.setup.projectMembers" />
                            </Link>
                        </Tooltip>
                    </Menu.Item>
                    <Menu.Item key="projectIssues" hidden={!auth.authorities.includes('ROLE_ISSUES')}>
                        <Tooltip
                            title={<FormattedMessage id="header.apps.setup.projectIssues.tooltip" />}
                            align={tooltipAlign}
                        >
                            <Link to="/setup/project-issues">
                                <FormattedMessage id="header.apps.setup.projectIssues" />
                            </Link>
                        </Tooltip>
                    </Menu.Item>
                    <Menu.Item key="projectSubmissions">
                        <Tooltip
                            title={<FormattedMessage id="header.apps.setup.projectSubmissions.tooltip" />}
                            align={tooltipAlign}
                        >
                            <Link to="/setup/project-submissions">
                                <FormattedMessage id="header.apps.setup.projectSubmissions" />
                            </Link>
                        </Tooltip>
                    </Menu.Item>

                    {auth.authorities.includes('ROLE_BIM360_ADMIN') && (
                        <Menu.Item key="members">
                            <Tooltip
                                title={<FormattedMessage id="header.apps.setup.members.tooltip" />}
                                align={tooltipAlign}
                            >
                                <Link to="/setup/members">
                                    <FormattedMessage id="header.apps.setup.members" />
                                </Link>
                            </Tooltip>
                        </Menu.Item>
                    )}
                </Menu>
            );
        } else if (auth && application === 'ADMIN') {
            return (
                <Menu theme="dark" mode="horizontal" defaultSelectedKeys={['users']} selectedKeys={selectedKeys}>
                    <Menu.Item key="users">
                        <Tooltip title={<FormattedMessage id="header.apps.admin.users.tooltip" />} align={tooltipAlign}>
                            <Link to="/admin/users">
                                <FormattedMessage id="header.apps.admin.users" />
                            </Link>
                        </Tooltip>
                    </Menu.Item>
                </Menu>
            );
        }
    };

    renderSettings = (): React.ReactElement | undefined => {
        const { auth } = this.context;
        const { pageId } = this.props;

        if (auth) {
            const tooltipAlign = { offset: [0, -19] };
            return (
                <>
                    <Tooltip title={<FormattedMessage id="header.apps.tasks.tooltip" />} align={tooltipAlign}>
                        <Link to="/tasks">
                            <Button
                                type="link"
                                className={`${styles.tasks} ${pageId === 'tasks' ? styles.active : ''}`}
                            >
                                <FormattedMessage id="header.apps.tasks" />
                            </Button>
                        </Link>
                    </Tooltip>
                    <Space split={<Divider type="vertical" className={styles.divider} />}>
                        {workDocsOAuth2Enabled ? (
                            <Link to="/settings" className={pageId === 'settings' ? styles.active : ''}>
                                <Icon component={UserSvg} className={styles.user} />
                                {this.context.auth?.firstName} {this.context.auth?.lastName}
                            </Link>
                        ) : (
                            <span>
                                <Icon component={UserSvg} className={styles.user} />
                                {this.context.auth?.firstName} {this.context.auth?.lastName}
                            </span>
                        )}
                        <span onClick={this.logOut} className={styles.signout}>
                            <FormattedMessage id="header.logOut" />
                        </span>
                    </Space>
                </>
            );
        }
    };

    render() {
        return (
            <div className={styles.header}>
                <div className={styles.logo}>{this.renderLogo()}</div>
                <div className={styles.apps}>{this.renderApps()}</div>
                <div className={styles.menu}>{this.renderMenu()}</div>
                <div className={styles.settings}>{this.renderSettings()}</div>
            </div>
        );
    }
}
export default injectIntl(HeaderComponent);

interface Props extends WrappedComponentProps {
    pageId?: PageType;
}

interface State {}
